import React from 'react';
import Header from '../components/header/Header.jsx';
import Footer from '../components/footer/Footer.jsx';
import FixedPlugin from '../components/fixedPlugin/FixedPlugin';

const PrivacyPolicy = () => {
	return (
		<div className='min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white'>
			<FixedPlugin />
			<Header />

			<main className='mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8'>
				<div className='mb-10'>
					<h1 className='text-4xl font-bold mb-3'>Privacy Policy</h1>
					<p className='text-gray-600 dark:text-gray-400'>Last updated: 26 October 2026</p>
				</div>

				<div className='space-y-10 text-gray-700 dark:text-gray-300 leading-relaxed'>
					<section>
						<p className='mb-4'>
							This Privacy Policy explains how HASNOVA Technologies Limited ("Company", "we", "our" or "us")
							collects, uses, shares, and protects personal data when you use ValuePay.ai (the "Website" and
							"Services"). We are committed to complying with the UK General Data Protection Regulation (UK GDPR)
							and the Data Protection Act 2018.
						</p>
						<p>
							By using the Website or Services, you agree to the collection and use of information in
							accordance with this policy.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>1. Information We Collect</h2>

						<h3 className='text-lg font-semibold mt-6 mb-2'>Account and identity data</h3>
						<p className='mb-2'>
							When you create an account, we collect your full name, email address, and company name, via
							Amazon Cognito, our authentication provider.
						</p>

						<h3 className='text-lg font-semibold mt-6 mb-2'>Company and project data</h3>
						<p className='mb-2'>
							We store information about your company, your team members, and the construction projects,
							Bills of Quantities (BOQ), and related documents you upload or generate using the Services.
						</p>

						<h3 className='text-lg font-semibold mt-6 mb-2'>Payment data</h3>
						<p className='mb-2'>
							Subscription payments are processed by Stripe, Inc. We do not collect or store your full card
							details ourselves — Stripe handles this directly and shares with us only what is necessary to
							manage your subscription, such as your plan, billing status, and a reference to your payment
							method.
						</p>

						<h3 className='text-lg font-semibold mt-6 mb-2'>Usage and technical data</h3>
						<p>
							We automatically collect certain technical information when you use the Website, including IP
							address, browser type, device information, and pages visited, typically via cookies and similar
							technologies (see Section 7, Cookies).
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>2. How We Use Your Information</h2>
						<p className='mb-2'>We use the personal data we collect to:</p>
						<ul className='list-disc pl-6 space-y-1'>
							<li>Create and manage your account and company workspace</li>
							<li>Provide the Services, including interim valuations, payment applications, and AI-assisted features</li>
							<li>Process subscription payments and manage billing</li>
							<li>Communicate with you about your account, updates, or support requests</li>
							<li>Monitor, maintain, and improve the security and performance of the Website</li>
							<li>Comply with our legal obligations</li>
						</ul>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>3. Legal Basis for Processing</h2>
						<p className='mb-2'>Under UK GDPR, we rely on the following legal bases to process your personal data:</p>
						<ul className='list-disc pl-6 space-y-1'>
							<li><strong>Contract</strong> — processing necessary to provide the Services you've signed up for</li>
							<li><strong>Legitimate interests</strong> — to secure, maintain, and improve the Website and Services</li>
							<li><strong>Consent</strong> — where you have given specific consent, such as for certain cookies</li>
							<li><strong>Legal obligation</strong> — where we must retain or disclose data to comply with the law</li>
						</ul>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>4. How We Share Your Information</h2>
						<p className='mb-2'>We do not sell your personal data. We share data only with:</p>
						<ul className='list-disc pl-6 space-y-1'>
							<li><strong>Stripe</strong> — to process subscription payments</li>
							<li><strong>Amazon Web Services (AWS)</strong> — to host the Website and store data securely</li>
							<li><strong>OpenAI</strong> — to power the Services' AI-assisted and generative features</li>
							<li>Other members of your company's workspace, as needed for the Services to function</li>
							<li>Law enforcement or regulators, where required by law</li>
						</ul>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>5. International Transfers</h2>
						<p>
							Some of our service providers, including OpenAI, may process data outside the UK. Where this
							happens, we rely on appropriate safeguards, such as Standard Contractual Clauses, to ensure your
							data continues to be protected to UK standards.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>6. Data Retention</h2>
						<p>
							We retain personal data for as long as your account remains active, and for a reasonable period
							afterwards to comply with our legal, accounting, or reporting obligations, or to resolve
							disputes. Payment records are retained in line with applicable financial record-keeping
							requirements.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>7. Cookies</h2>
						<p>
							The Website uses cookies and similar technologies to keep you signed in, remember your
							preferences, and understand how the Website is used. You can manage or disable cookies through
							your browser settings, though this may affect the functionality of the Website.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>8. Security</h2>
						<p>
							We use industry-standard technical and organisational measures, including encryption in transit
							and at rest, to protect your personal data. However, no method of transmission or storage is
							completely secure, and we cannot guarantee absolute security.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>9. Your Rights</h2>
						<p className='mb-2'>
							Under UK GDPR, you have the right to:
						</p>
						<ul className='list-disc pl-6 space-y-1 mb-4'>
							<li>Access the personal data we hold about you</li>
							<li>Request correction of inaccurate data</li>
							<li>Request erasure of your data, subject to certain exceptions</li>
							<li>Restrict or object to our processing of your data</li>
							<li>Request a portable copy of your data</li>
							<li>Withdraw consent at any time, where processing is based on consent</li>
						</ul>
						<p>
							To exercise any of these rights, please contact us using the details in Section 12. You also
							have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at{' '}
							<a href='https://ico.org.uk' className='text-blue-600 hover:underline' target='_blank' rel='noreferrer'>
								ico.org.uk
							</a>.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>10. Children's Privacy</h2>
						<p>
							The Services are intended for business use and are not directed at children. We do not
							knowingly collect personal data from anyone under the age of 18.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>11. Changes to This Policy</h2>
						<p>
							We may update this Privacy Policy from time to time. Any changes will be posted on this page
							with an updated "Last updated" date. Continued use of the Website after changes are posted
							constitutes acceptance of the revised policy.
						</p>
					</section>

					<section>
						<h2 className='text-2xl font-bold mb-4'>12. Contact Us</h2>
						<p className='mb-2'>
							If you have any questions about this Privacy Policy or wish to exercise your data protection
							rights, please contact us at:
						</p>
						<div className='bg-gray-50 dark:bg-gray-800/50 border border-gray-800/40 dark:border-gray-600/25 rounded-lg p-6 mt-4'>
							<p className='font-semibold'>HASNOVA Technologies Limited</p>
							<p>Email: info@valuepay.ai</p>
							<p>Office: 123 Innovation Park, London, UK</p>
						</div>
					</section>
				</div>
			</main>

			<Footer />
		</div>
	);
};

export default PrivacyPolicy;
